import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import { CharterStatusBadgeComponent } from '@features/dashboard/company/corporate-charter/components/charter-status-badge/charter-status-badge.component';
import {
  corporateCharterRoute,
  formatCnpj,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import {
  CharterCompanySearchService,
  CharterSwitcherCurrent,
  CharterSwitcherItem,
} from '@features/dashboard/company/corporate-charter/services/charter-company-search.service';
import { Subject, catchError, debounce, of, switchMap, tap, timer } from 'rxjs';

let nextSwitcherId = 0;

/**
 * Último item do caminho da tela Societário da empresa: o nome da empresa vira um seletor. Abre um popover com busca
 * (nome ou CNPJ, com espera de 250 ms) nas empresas do escopo, cada uma com o selo da situação do contrato social, e
 * leva para `/dashboard/company/<id>/societario` mantendo a aba aberta (a página é reaproveitada pelo roteador).
 * Teclado: seta para baixo abre, setas navegam, Enter escolhe, Esc fecha. "Limpar seleção" volta para a lista. Papel COMPANY com uma empresa só vê o nome
 * sem seletor.
 */
@Component({
  selector: 'app-charter-company-switcher',
  imports: [SharedModule, CharterStatusBadgeComponent],
  providers: [CharterCompanySearchService],
  templateUrl: './charter-company-switcher.component.html',
  styleUrl: './charter-company-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:pointerdown)': 'onDocumentPointerDown($event)' },
})
export class CharterCompanySwitcherComponent {
  readonly companyId = input<string | null>(null);
  readonly companyName = input<string | null>(null);
  readonly companyCnpj = input<string | null>(null);

  private readonly search = inject(CharterCompanySearchService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly list = viewChild<ElementRef<HTMLElement>>('list');

  protected readonly canSwitch = toSignal(this.search.canSwitch(), { initialValue: false });
  protected readonly open = signal(false);
  protected readonly term = signal('');
  protected readonly loading = signal(false);
  protected readonly failed = signal(false);
  protected readonly items = signal<CharterSwitcherItem[]>([]);
  protected readonly total = signal(0);
  protected readonly activeIndex = signal(-1);

  protected readonly listId = `charter-switcher-list-${nextSwitcherId++}`;
  protected readonly formatCnpj = formatCnpj;
  protected readonly skeletonRows = [0, 1, 2, 3];

  private readonly query$ = new Subject<{ term: string; immediate: boolean }>();

  constructor() {
    this.query$
      .pipe(
        debounce(query => timer(query.immediate ? 0 : 250)),
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
        }),
        switchMap(query =>
          this.search.search(query.term, this.current()).pipe(
            catchError(() => {
              this.failed.set(true);
              return of(null);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        const items = result?.items ?? [];
        this.items.set(items);
        this.total.set(result?.total ?? 0);
        this.activeIndex.set(this.initialIndex(items));
        this.loading.set(false);
        this.scrollActiveIntoView();
      });
  }

  private current(): CharterSwitcherCurrent | null {
    const companyId = this.companyId();
    return companyId
      ? { companyId, companyName: this.companyName(), cnpj: this.companyCnpj() }
      : null;
  }

  protected optionId(index: number): string {
    return `${this.listId}-opt-${index}`;
  }

  protected toggle(): void {
    if (this.open()) {
      this.close(false);
    } else {
      this.openPopover();
    }
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' && !this.open()) {
      event.preventDefault();
      this.openPopover();
    }
  }

  protected onTermInput(value: string): void {
    this.term.set(value);
    this.query$.next({ term: value, immediate: false });
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    const count = this.items().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) {
          this.activeIndex.set(Math.min(count - 1, this.activeIndex() + 1));
          this.scrollActiveIntoView();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) {
          this.activeIndex.set(Math.max(0, this.activeIndex() - 1));
          this.scrollActiveIntoView();
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const item = this.items()[this.activeIndex()];
        if (item && !this.loading()) {
          this.select(item);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        break;
      case 'Tab':
        this.close(false);
        break;
    }
  }

  protected select(item: CharterSwitcherItem): void {
    this.close(true);
    if (item.companyId !== this.companyId()) {
      this.router.navigate(corporateCharterRoute(item.companyId));
    }
  }

  /** "x" da busca: limpa o termo, recarrega a lista e devolve o foco ao campo. */
  protected clearTerm(): void {
    this.term.set('');
    this.query$.next({ term: '', immediate: true });
    this.searchInput()?.nativeElement.focus();
  }

  /** "Limpar seleção": sai da empresa e volta para a lista Societário. */
  protected clearSelection(): void {
    this.close(false);
    this.router.navigate(['/dashboard/company/societario']);
  }

  protected retry(): void {
    this.query$.next({ term: this.term(), immediate: true });
  }

  protected onDocumentPointerDown(event: PointerEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  private openPopover(): void {
    this.open.set(true);
    this.term.set('');
    this.items.set([]);
    this.query$.next({ term: '', immediate: true });
    afterNextRender(() => this.searchInput()?.nativeElement.focus(), { injector: this.injector });
  }

  private close(returnFocus: boolean): void {
    this.open.set(false);
    if (returnFocus) {
      this.trigger()?.nativeElement.focus();
    }
  }

  /** Começa na empresa aberta, se ela estiver na lista; senão, na primeira. */
  private initialIndex(items: CharterSwitcherItem[]): number {
    if (!items.length) {
      return -1;
    }
    const current = items.findIndex(item => item.companyId === this.companyId());
    return this.term().trim() || current < 0 ? 0 : current;
  }

  private scrollActiveIntoView(): void {
    afterNextRender(
      () => {
        const index = this.activeIndex();
        this.list()
          ?.nativeElement.querySelector(`#${this.optionId(index)}`)
          ?.scrollIntoView({ block: 'nearest' });
      },
      { injector: this.injector }
    );
  }
}
